import { supabase } from "../../../lib/supabase.ts";
import { FieldSetSchema } from "../../../lib/schemas/index.ts";
import { logger } from "../../../lib/logger.ts";
import type { EventConfig, EventConfigRow } from "../types.ts";

/**
 * Read the curated public view (anon + RLS; open events only). Returns null when the
 * slug isn't an open event (closed / archived / nonexistent all look the same to anon,
 * by design). Throws only on an unexpected transport/config error.
 */
export async function fetchEventConfig(slug: string): Promise<EventConfig | null> {
  const { data, error } = await supabase
    .from("public_event_view")
    .select("*")
    .eq("slug", slug)
    .maybeSingle<EventConfigRow>();

  if (error) {
    logger.error("event_fetch_failed", { slug, code: error.code });
    throw new Error("event_fetch_failed");
  }
  if (!data) return null;

  // A malformed field set must not render a broken form — fail loud (caught upstream).
  const parsed = FieldSetSchema.safeParse(data.field_set);
  if (!parsed.success) {
    logger.error("event_field_set_invalid", { slug });
    throw new Error("event_field_set_invalid");
  }

  return {
    id: data.id,
    slug: data.slug,
    name: data.name,
    description: data.description,
    brand_display_name: data.brand_display_name,
    brand_logo_url: data.brand_logo_url,
    brand_accent_color: data.brand_accent_color,
    consent_copy: data.consent_copy,
    consent_version: data.consent_version,
    incentive_type: data.incentive_type,
    incentive_label: data.incentive_label,
    status: data.status,
    fieldSet: parsed.data,
  };
}
